import React, { useState, useEffect } from 'react';
import { FiClock, FiCoffee, FiCalendar, FiAlertTriangle, FiPhone, FiLayers } from 'react-icons/fi';
import api from '../../services/api';

const OfficePolicyPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await api.get('/policies');
      setPolicies(response.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      'FiClock': FiClock,
      'FiCoffee': FiCoffee,
      'FiCalendar': FiCalendar,
      'FiAlertTriangle': FiAlertTriangle,
      'FiPhone': FiPhone,
      'FiLayers': FiLayers
    };
    return icons[iconName] || FiClock;
  };

  const getColorClass = (color) => {
    const colors = {
      'blue': 'text-blue-500 border-blue-400 bg-blue-50',
      'green': 'text-green-500 border-green-400 bg-green-50',
      'purple': 'text-purple-500 border-purple-400 bg-purple-50',
      'orange': 'text-orange-500 border-orange-400 bg-orange-50',
      'red': 'text-red-500 border-red-400 bg-red-50',
      'yellow': 'text-yellow-500 border-yellow-400 bg-yellow-50'
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-gray-900">Office Policy</h1>
        <div className="text-center py-8">Loading policies...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Office Policy</h1>
        <p className="text-gray-600">Please read and follow these office policies carefully</p>
      </div>

      <div className="space-y-6">
        {/* Team Notice Policy */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <FiCalendar className="text-blue-500 mr-3" size={24} />
            <h2 className="text-lg font-semibold text-gray-900">Team Notice</h2>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <div className="text-gray-800 whitespace-pre-line">
              Please note that the 1st and 3rd Saturdays of every month will be holidays.
              However, in case of any project emergency, you may be required to come to the office.
              For such emergency work, extra payment will be provided for the day.
              Please note that you cannot take leave on the next day as compensation for this work.
              Personal leave should be applied at least 1 week in advance.
              Emergency leave can be taken when needed, but you must inform Shailendra Ji beforehand.
              Uninformed leave will result in 3 days of leave being applied automatically.
            </div>
          </div>
        </div>

        {/* Sandwich Rule Policy */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <FiLayers className="text-orange-500 mr-3" size={24} />
            <h2 className="text-lg font-semibold text-gray-900">Sandwich Rule – Detailed Explanation</h2>
          </div>
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
            <div className="text-gray-800 space-y-4">
              <p>The Sandwich Rule is applied to ensure fairness and attendance discipline when employees take leaves around holidays or weekends. It combines holidays and weekly offs between two leaves as part of the total leave count.</p>
              
              <div>
                <h4 className="font-semibold mb-2">1. Basic Rule</h4>
                <p>If a holiday or weekend falls between two leave days, it will also be counted as leave.</p>
                <p className="text-sm text-gray-600 mt-1"><strong>Example:</strong> If Friday and Monday are marked as leave, and Saturday–Sunday are weekly offs, then total 4 days (Friday, Saturday, Sunday, Monday) will be counted as leave.</p>
              </div>
              

              
              <div>
                <h4 className="font-semibold mb-2">3. Continuous Holiday Period</h4>
                <p>If there are multiple consecutive holidays, and you take leave on both sides of that block, then the entire period (including holidays) will be treated as leave.</p>
                <p className="text-sm text-gray-600 mt-1"><strong>Example:</strong> If Thursday and Tuesday are leave, and Friday to Monday are holidays, then all 6 days (Thursday–Tuesday) will be counted as leave.</p>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded p-3 mt-4">
                <p className="text-red-800 font-semibold">This will apply from 1 November 2025</p>
                <p className="text-red-700 text-sm mt-1">All team members are expected to follow these guidelines strictly and plan their leaves responsibly. Failure to comply may lead to loss of pay or disciplinary action.</p>
              </div>
            </div>
          </div>
        </div>

        {policies.map((policy) => {
          const IconComponent = getIcon(policy.icon);
          const colorClasses = getColorClass(policy.color);
          const [textColor, borderColor, bgColor] = colorClasses.split(' ');
          
          return (
            <div key={policy.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <IconComponent className={`${textColor} mr-3`} size={24} />
                <h2 className="text-lg font-semibold text-gray-900">{policy.title}</h2>
              </div>
              <div className={`${bgColor} border-l-4 ${borderColor} p-4 rounded`}>
                <div 
                  className="prose prose-sm max-w-none text-gray-800"
                  dangerouslySetInnerHTML={{ __html: policy.content }}
                />
              </div>
            </div>
          );
        })}

        {/* Important Notice */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Important Notice</h3>
          <div className="space-y-2 text-gray-700">
            <p>• All policies are subject to company discretion and may be updated from time to time.</p>
            <p>• Employees are expected to maintain professionalism and adhere to all company policies.</p>
            <p>• Any violations of these policies may result in disciplinary action.</p>
            <p>• For clarifications or questions, please contact HR department.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficePolicyPage;